import React from 'react';
import useGameStore from '../engine/store';
import { formatPrice } from '../engine/scenarios';

function TensionMeter({ level }) {
  const pct = Math.round(level * 100);
  const color = pct > 70 ? '#ff3366' : pct > 40 ? '#ffcc00' : '#00aaff';
  const label = pct > 80 ? 'CRITICAL' : pct > 60 ? 'HIGH' : pct > 35 ? 'RISING' : 'CALM';

  return (
    <div className="glass-panel p-3">
      <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">Tension</div>
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2 rounded-full bg-dark-600 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${pct}%`,
              background: `linear-gradient(90deg, #00aaff, ${color})`,
              boxShadow: pct > 60 ? `0 0 10px ${color}40` : 'none',
            }}
          />
        </div>
        <span className="text-xs font-mono font-bold" style={{ color }}>{label}</span>
      </div>
    </div>
  );
}

function Timer({ seconds, total }) {
  const pct = total > 0 ? (seconds / total) * 100 : 0;
  const color = pct > 50 ? '#00ff88' : pct > 25 ? '#ffcc00' : '#ff3366';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;

  return (
    <div className="glass-panel p-3">
      <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">Time Left</div>
      <div className="flex items-center gap-2">
        <div className="text-xl font-black font-mono" style={{ color }}>
          {mins}:{secs.toString().padStart(2, '0')}
        </div>
        <div className="flex-1 h-2 rounded-full bg-dark-600 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-1000"
            style={{
              width: `${pct}%`,
              background: color,
            }}
          />
        </div>
      </div>
    </div>
  );
}

function DealPosition() {
  const { scenario, currentOffer, opponentLastOffer, playerRole, playerLastOffer } = useGameStore();
  if (!scenario) return null;

  const target = scenario.playerTarget;
  const list = scenario.listPrice;
  const opp = opponentLastOffer || list;
  const isBuyer = playerRole === 'buyer';

  // How close are we to our target?
  const range = Math.abs(target - list);
  let progressPct;
  if (isBuyer) {
    progressPct = range > 0 ? Math.max(0, Math.min(100, ((list - opp) / range) * 100)) : 0;
  } else {
    progressPct = range > 0 ? Math.max(0, Math.min(100, ((opp - list) / range) * 100)) : 0;
  }

  return (
    <div className="glass-panel p-3">
      <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Deal Position</div>

      {/* Visual position bar */}
      <div className="relative h-6 rounded-full bg-dark-600 overflow-hidden mb-2">
        <div
          className="absolute left-0 h-full rounded-full transition-all duration-500"
          style={{
            width: `${progressPct}%`,
            background: progressPct > 70 ? 'linear-gradient(90deg, #00aaff, #00ff88)' :
              progressPct > 40 ? 'linear-gradient(90deg, #00aaff, #ffcc00)' :
              'linear-gradient(90deg, #00aaff, #ff8800)',
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white z-10">
          {formatPrice(opp, scenario)}
        </div>
      </div>

      <div className="flex justify-between text-[10px]">
        <div>
          <span className="text-gray-500">{isBuyer ? 'Listed' : 'Offered'}: </span>
          <span className="text-gray-400 font-mono">{formatPrice(list, scenario)}</span>
        </div>
        <div>
          <span className="text-gray-500">Target: </span>
          <span className="text-neon-green font-mono">{formatPrice(target, scenario)}</span>
        </div>
      </div>
    </div>
  );
}

function ScenarioInfo() {
  const { scenario, round, playerRole } = useGameStore();
  if (!scenario) return null;

  return (
    <div className="glass-panel p-3">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xl">{scenario.emoji}</span>
        <span className="font-bold text-white text-sm">{scenario.title}</span>
      </div>
      <div className="flex items-center gap-3 text-[10px]">
        <span className={`px-2 py-0.5 rounded-full ${
          playerRole === 'buyer' ? 'bg-neon-blue/20 text-neon-blue' : 'bg-neon-green/20 text-neon-green'
        }`}>
          {playerRole === 'buyer' ? '🛒 Buyer' : '💰 Seller'}
        </span>
        <span className="text-gray-500">Round {round}</span>
        <span className="text-gray-600">Difficulty: {'⭐'.repeat(scenario.difficulty)}</span>
      </div>
    </div>
  );
}

export default function StatusBar() {
  const { timeRemaining, timeLimit, tensionLevel } = useGameStore();

  return (
    <div className="p-3 space-y-2 overflow-y-auto">
      <ScenarioInfo />
      <Timer seconds={timeRemaining} total={timeLimit} />
      <TensionMeter level={tensionLevel} />
      <DealPosition />
    </div>
  );
}
